<script lang="ts">
	import { clone, type JsonValue } from "@bufbuild/protobuf";
	import { Label, Select } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import { PageSchema, type Page } from "#gen/pages/v2/page_pb.js";
	import P from "#lib/components/P.svelte";
	import ColorInputV2 from "#lib/components/flow/editor/appearance/ColorInputV2.svelte";
	import { ELEMENT_DEFAULTS, FONT_WEIGHTS, HORIZONTAL_ALIGNMENTS, TEXT_SIZES, type TextElement } from "#lib/components/flow/editor/appearance/constants.js";
	import type { JsonObject } from "#lib/protobuf-compat.js";
	import { setStringValue, setStructValue, stringValue, structValue } from "#lib/struct.js";
	import { getHeaderTextColor, getParagraphTextColor } from "#lib/theme.js";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		page: Writable<Page>;
		onChange: (value?: JsonValue) => void;
		defaultElement?: string;
	}

	let { id, page, onChange, defaultElement = "p" }: Props = $props();

	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");

	const textElement = $derived((stringValue(structValue($page.config, id), "element") ?? defaultElement) as TextElement);
	const textAlignment = $derived(stringValue(structValue($page.config, id), "textAlignment") ?? ELEMENT_DEFAULTS[textElement].textAlignment);
	const textSize = $derived(stringValue(structValue($page.config, id), "textSize") ?? ELEMENT_DEFAULTS[textElement].textSize);
	const textColor = $derived(stringValue(structValue($page.config, id), "textColor"));
	const fontWeight = $derived(stringValue(structValue($page.config, id), "fontWeight") ?? ELEMENT_DEFAULTS[textElement].fontWeight);

	function getConfig(): JsonObject {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		return config;
	}

	function getThemeColorForElement(element: string) {
		if (element === "h1" || element === "h2") {
			return getHeaderTextColor($brandKit, $page);
		}
		return getParagraphTextColor($brandKit, $page);
	}

	function handleChangeAppearance(e: Event, key: string) {
		const config = getConfig();
		const value = (e.target as HTMLSelectElement).value;
		if (value) {
			setStringValue(config, key, value);
		} else {
			delete config[key];
		}
		$page = $page;
		onChange?.();
	}

	function resetToPageStyle(key: string) {
		const config = structValue($page.config, id);
		if (config) {
			delete config[key];
			$page = $page;
			onChange?.();
		}
	}

	function handleColorInput(value: string) {
		const config = getConfig();
		if (value) {
			setStringValue(config, "textColor", value);
			$page = clone(PageSchema, $page);
		}
	}
</script>

<div class="flex flex-col gap-5">
	<P class="text-sm font-bold text-black">Appearance</P>
	<div class="flex flex-col justify-between gap-2 border-l-[5px] border-l-gray-300 pl-6">
		<div class="flex flex-row gap-2">
			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<Label for="{id}-alignment-label" class="font-medium text-black">Text alignment</Label>
				<Select classes={{ select: "truncate" }} items={HORIZONTAL_ALIGNMENTS} value={textAlignment} aria-labelledby="{id}-alignment-label" onchange={e => handleChangeAppearance(e, "textAlignment")} />
			</div>
			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<Label for="{id}-size-label" class="font-medium text-black">Text size</Label>
				<Select classes={{ select: "truncate" }} items={TEXT_SIZES} value={textSize} aria-labelledby="{id}-size-label" onchange={e => handleChangeAppearance(e, "textSize")} />
			</div>
		</div>
		<div class="flex flex-row gap-2">
			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<Label for="{id}-color-label" class="font-medium text-black">Text color</Label>
				<ColorInputV2 id="{id}-color-label" resetColor="#000000" display="inline" value={textColor || getThemeColorForElement(textElement)} showBrandKit={false} overridden={Boolean(textColor)} onReset={() => resetToPageStyle("textColor")} oninput={handleColorInput} onchange={value => onChange?.(value)} />
			</div>
			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<Label for="{id}-font-weight-label" class="font-medium text-black">Font weight</Label>
				<Select classes={{ select: "truncate" }} items={FONT_WEIGHTS} value={fontWeight} aria-labelledby="{id}-font-weight-label" onchange={e => handleChangeAppearance(e, "fontWeight")} />
			</div>
		</div>
	</div>
</div>
